import { useState } from 'react';
import { Stack, Tab, Tabs, Title } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { DefaultErrorHandler, ModalSidePanel } from '@mews-commander/ui';
import { ActorProfilesSection, CancellationPolicyName } from '../../../../../components';
import { CancellationPolicyDetail } from '../../../../cancellationPolicies';
import { useCentralCancellationPolicyGet } from '../../queries';
import { EditCancellationPolicyButton } from './EditCancellationPolicyButton';
import { PropertiesTabContent } from './PropertiesTabContent';
import { HeaderActions } from './HeaderActions';

enum CancellationPolicyTabValues {
    Details = 'details',
    Properties = 'properties',
}
interface Props {
    cancellationPolicyId?: string;
    closeSidePanel: () => void;
}

export const CancellationPolicySidePanel = ({ cancellationPolicyId, closeSidePanel }: Props) => {
    const [activeTab, setActiveTab] = useState<CancellationPolicyTabValues>(CancellationPolicyTabValues.Details);
    const { data, isFetching, isError } = useCentralCancellationPolicyGet(cancellationPolicyId);

    const content = data ? (
        <Stack vertical spacing="400">
            <Tabs
                activeTabValue={activeTab}
                inline
                onTabClick={(_, value) => setActiveTab(value as CancellationPolicyTabValues)}
                showDivider
            >
                <Tab
                    id={CancellationPolicyTabValues.Details}
                    label={lt('DetailPlural')}
                    value={CancellationPolicyTabValues.Details}
                />
                <Tab
                    id={CancellationPolicyTabValues.Properties}
                    label={lt('Properties')}
                    value={CancellationPolicyTabValues.Properties}
                />
            </Tabs>
            {activeTab === CancellationPolicyTabValues.Details ? (
                <Stack vertical spacing="400">
                    <CancellationPolicyDetail
                        {...data}
                        key={undefined}
                        policyKey={data?.key}
                        applicabilityType={data.applicability}
                        rightContent={<EditCancellationPolicyButton cancellationPolicyId={data.id} />}
                    />
                    <ActorProfilesSection
                        createdUtc={data.createdUtc}
                        creatorProfile={{ name: data.creatorProfileName }}
                        updatedUtc={data.updatedUtc}
                        updaterProfile={{ name: data.updaterProfileName }}
                    />
                </Stack>
            ) : null}
            {activeTab === CancellationPolicyTabValues.Properties ? <PropertiesTabContent data={data} /> : null}
        </Stack>
    ) : null;

    return (
        <ModalSidePanel
            content={isError ? <DefaultErrorHandler /> : content}
            loading={isFetching}
            headerActions={
                data ? <HeaderActions cancellationPolicyId={data.id} closeSidePanel={closeSidePanel} /> : null
            }
            onClose={() => {
                closeSidePanel();
                setActiveTab(CancellationPolicyTabValues.Details);
            }}
            opened={!!cancellationPolicyId}
            title={
                data && (
                    <Title level={2} wordBreak="break-all" ellipsis maxLines={2}>
                        <CancellationPolicyName {...data} applicabilityType={data.applicability} />
                    </Title>
                )
            }
            width="medium"
        />
    );
};
